import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLObjectTypes, TpETLTableColumn } from '../../etl-data-types/interfaces';
import {
  TpETLPipelineComponent,
  TpETLDryRun,
  TpETLDryRunComponentResult,
  TpETLDryRunTableObject,
} from '../../interfaces';
import {
  copyETLDryRunObjectTableColumns,
  createConfigParamTypeExistingColumn,
  createConfigParamTypeMultipleChoice,
  createConfigParamTypeMultipleChoiceList,
  ETLDryRunObjectTableAddColumn,
} from '../utils';
import { addDryRunObjectTable, getETLComponentInputObjectTable, getETLTableObjectColumn } from '../../utils';
import { addETLDryRunError } from '../../etlDryRun';
import { VALTYPES } from '../../../../data/interfaces';
import { CONFIGPARAMTYPES } from '../../../visual-analytics/helpers/configParams';

type TpFieldOptions = {
  id: string;
  name: string;
  multiValue?: boolean;
};

const COMMON_SPECIES_NAMES: string[] = [
  'human',
  'mouse',
  'rat',
  'fruitfly',
  'nematode',
  'zebrafish',
  'thale-cress',
  'frog',
  'pig',
];

const GENE_INFORMATION_FIELDS: TpFieldOptions[] = [
  { id: 'map_location', name: 'Cytogenetic Location' },
  { id: 'ensembl.protein', name: 'Ensembl protein id(s)', multiValue: true },
  { id: 'ensembl.gene_primary', name: 'Ensembl gene id (primary)' }, // Not a normal search field, will be handled separately
  { id: 'ensembl.gene', name: 'Ensembl gene ids (all)', multiValue: true },
  { id: 'ensembl.transcript', name: 'Ensembl transcript id(s)', multiValue: true },
  { id: 'entrezgene', name: 'Entrez gene id' },
  { id: 'alias', name: 'Gene alias', multiValue: true },
  { id: 'name', name: 'Gene name' },
  { id: 'summary', name: 'Gene summary text' },
  { id: 'genomic_pos.chr', name: 'Genomic Position (Chromosome)' },
  { id: 'genomic_pos.start', name: 'Genomic Position (Start)' },
  { id: 'genomic_pos.end', name: 'Genomic Position (End)' },
  { id: 'genomic_pos.strand', name: 'Genomic Position (Strand)' },
  { id: 'unigene', name: 'NCBI UniGene id(s)', multiValue: true },
  { id: 'symbol', name: 'Official gene symbol' },
  { id: 'retired', name: 'Retired Entrez gene id(s)', multiValue: true },
  { id: 'uniprot.Swiss-Prot', name: 'UniProt Swiss-Prot id(s)', multiValue: true },
  { id: 'uniprot.TrEMBL', name: 'UniProt TrEMBL id(s)', multiValue: true },
];

// These columns were added automatically to the NormalizeGeneNames component before it was parametrized.
const DEFAULT_COLUMN_OPTIONS = ['symbol', 'ensembl.gene_primary'];

// These columns were added by default before the component had the fields parameter.
// Add these in case the fields parameter is not present
function addDefaultColumns(outputTableObj: TpETLDryRunTableObject, soureceColInfo: TpETLTableColumn) {
  ETLDryRunObjectTableAddColumn(
    outputTableObj,
    `${soureceColInfo.identifier}_normalized`,
    `${soureceColInfo.name} (Normalized)`,
    `${soureceColInfo.description} (Normalized)`,
    soureceColInfo.dataType,
  );
  const isPlural = soureceColInfo.dataType === VALTYPES.MULTICAT || soureceColInfo.dataType === VALTYPES.MULTISTRING;
  ETLDryRunObjectTableAddColumn(
    outputTableObj,
    isPlural ? `ensembl_ids` : `ensembl_id`,
    isPlural ? `Ensembl ids` : `Ensembl id`,
    isPlural ? `Ensembl ids` : `Ensembl id`,
    soureceColInfo.dataType,
  );
}

/**
 * Validates a comma-separated list of species names or NCBI taxonomy IDs (pure digits).
 * @param species A comma-separated string of species names or IDs.
 * @returns True if the list is valid, false otherwise.
 */
function isValidSpeciesList(species: string): boolean {
  const speciesList = species.split(',').map((s) => s.trim());
  return speciesList.every((s) => COMMON_SPECIES_NAMES.includes(s) || /^\d+$/.test(s));
}

function getColumnDataType(sourceColInfo: TpETLTableColumn, multiValue: boolean, duplicateMethod: string): string {
  multiValue = multiValue || duplicateMethod === 'multi_value'; // Always have multiValue if duplicateMethod is multi_value
  if (sourceColInfo.dataType === VALTYPES.MULTICAT || sourceColInfo.dataType === VALTYPES.MULTISTRING) {
    return sourceColInfo.dataType; // Keep multi-value if the source is multi-value
  } else if (sourceColInfo.dataType === VALTYPES.CATEGORICAL) {
    return multiValue ? VALTYPES.MULTICAT : VALTYPES.CATEGORICAL;
  }
  return multiValue ? VALTYPES.MULTISTRING : VALTYPES.STRING;
}

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const inputTableObj = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');
  const outputTableObj = addDryRunObjectTable(dryRun, comp.outputObjectIds['result'], comp);

  copyETLDryRunObjectTableColumns(inputTableObj, outputTableObj);

  const duplicateMethod = comp.params['handleDuplicates'];
  if (duplicateMethod === '') {
    addETLDryRunError(dryRun, `Please select a behaviour in case of duplicates`, comp.id, 'handleDuplicates');
  }

  const scopes: string[] | null | undefined = comp.params['scopes'];
  if (scopes && scopes.length === 0) {
    addETLDryRunError(dryRun, `Please select at least one lookup field identifier`, comp.id, 'scopes');
  }

  const infoFields: string[] | null | undefined = comp.params['fields'];
  if (infoFields && infoFields.length === 0) {
    addETLDryRunError(dryRun, `Please select at least one gene information field`, comp.id, 'fields');
  }

  const species: string | null | undefined = comp.params['species'];
  if (species && !isValidSpeciesList(species)) {
    addETLDryRunError(
      dryRun,
      `Invalid species list. Please provide a comma separated list of either common species names or NCBI taxonomy ids.`,
      comp.id,
      'species',
    );
  }

  const sourceGeneNamesColId = comp.params['geneNamesInput'];
  if (!sourceGeneNamesColId) {
    addETLDryRunError(dryRun, `Missing source gene names column`, comp.id, 'geneNamesInput');
    return { abort: false };
  }
  const soureceColInfo = getETLTableObjectColumn(inputTableObj, sourceGeneNamesColId);

  // Add the columns to the dryrun output
  if (!infoFields) {
    // No fields parameter present: add the default columns for backward compatibility
    addDefaultColumns(outputTableObj, soureceColInfo);
  } else {
    infoFields.forEach((field) => {
      const colInfo = GENE_INFORMATION_FIELDS.find((f) => f.id === field);
      if (!colInfo) {
        throw `Internal error: unknown field ${field}`;
      }
      const sanitizedField = field.replace(/\./g, '_').toLowerCase();

      ETLDryRunObjectTableAddColumn(
        outputTableObj,
        `${soureceColInfo.identifier}_${sanitizedField}`,
        colInfo.name,
        '', // No description yet
        getColumnDataType(soureceColInfo, !!colInfo.multiValue, duplicateMethod),
      );
    });
  }

  return { abort: false };
}

export const manifestNormalizeGeneNames: TpETLComponentTypeManifest = {
  id: 'NormalizeGeneNames',
  name: 'Gene Information',
  description: 'Given a column with some gene identifiers, look up additional information about those genes.',
  categoryId: 'biology',
  icon: <FontAwesomeIcon icon={['fad', 'dna']} />,
  inputs: [
    {
      type: TpETLObjectTypes.TABLE,
      id: 'sourceTable',
      name: 'Source Table',
      columns: [],
    },
  ],
  outputs: [
    {
      id: 'result',
      name: 'Result table',
      type: TpETLObjectTypes.TABLE,
      description: 'Table with additional gene information added',
      columns: [],
    },
  ],
  params: [
    {
      id: 'geneNamesInput',
      name: 'Source gene names',
      description: 'Column used containing the source gene identifiers.',
      paramType: createConfigParamTypeExistingColumn('sourceTable'),
    },
    {
      id: 'scopes',
      name: 'Lookup field identifiers',
      description: 'One or more fields to use for looking up gene information.',
      paramType: createConfigParamTypeMultipleChoiceList(
        [
          { id: 'ensembl.protein', name: 'Ensembl protein id' },
          { id: 'ensembl.gene', name: 'Ensembl gene id' },
          { id: 'ensembl.transcript', name: 'Ensembl transcript id' },
          { id: 'entrezgene', name: 'Entrez gene id' },
          { id: 'alias', name: 'Gene alias' },
          { id: 'name', name: 'Gene name' },
          { id: 'summary', name: 'Gene summary text' },
          { id: 'accession', name: 'NCBI GeneBank Accession number' },
          { id: 'homologene', name: 'NCBI HomoloGene id' },
          { id: 'refseq', name: 'NCBI RefSeq id' },
          { id: 'unigene', name: 'NCBI UniGene id' },
          { id: 'symbol', name: 'Official gene symbol' },
          { id: 'retired', name: 'Retired Entrez gene ids' },
          { id: 'uniprot', name: 'UniProt id' },
        ],
        ['symbol', 'alias', 'accession'], // Defaults
      ),
    },
    {
      id: 'fields',
      name: 'Gene information to provide',
      description:
        'One of more information fields to add as new columns. The datatype is identical to the source column or a multi-value column for some fields.',
      paramType: createConfigParamTypeMultipleChoiceList(
        GENE_INFORMATION_FIELDS.map((field) => ({
          id: field.id,
          name: field.multiValue ? `${field.name} (multi-value)` : field.name,
        })),
        DEFAULT_COLUMN_OPTIONS,
      ),
    },
    {
      id: 'species',
      name: 'Species to use for search',
      description: `Limit the gene hits from given species to comma separated list of names. \
       You can use “common names” for nine common species \
       (${COMMON_SPECIES_NAMES.join(', ')}). \
       All other species, you can provide their NCBI taxonomy ids.`,
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.String,
        defaultValue: 'human',
      },
    },
    {
      id: 'entrezOnly',
      name: 'Entrez Gene IDs only',
      description:
        'If enabled, return only matching entrez genes. Otherwise, include also matching Ensemble-only genes (those have no matching entrez genes)',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.Boolean,
        defaultValue: false,
        label: 'Limit to Entrez Gene IDs',
      },
    },
    {
      id: 'handleDuplicates',
      name: 'Handle Duplicates',
      description: 'Method to handle duplicate gene entries.',
      paramType: createConfigParamTypeMultipleChoice(
        [
          {
            id: 'skip',
            name: 'Skip entries with duplicate hits',
          },
          {
            id: 'first_hit',
            name: 'Take first hit',
          },
          {
            // concatenate and multi_value look the same but (can) have a different datatype
            id: 'concatenate',
            name: 'Concatenate results in a semi-colon separated string',
          },
          {
            id: 'multi_value',
            name: 'Return all results as multi-value columns',
          },
        ],
        'first_hit', // Default
      ),
    },
  ],
  performDryRun,
};
